<script setup>
import NotificationHighlight from './notification/Highlight.vue';
import { Loading as NNerveLoading } from '@bignerve/ui-core';
import { UseVirtualList, vInfiniteScroll } from '@vueuse/components';
import { computed, watch } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { useBus } from '/app/composables/bus';
import { useHighlights } from '/app/queries/gamedash';

const props = defineProps({
    role: {
        type: String,
        default: 'overall',
    },
    height: {
        type: String,
        default: '160px',
    },
});

const { on, emit: busEmit } = useBus();
const queryClient = useQueryClient();

const params = computed(() => ({ role: props.role, limit: 15 }));

const {
    data: highlights,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
} = useHighlights(params);

function loadMore() {
    if (hasNextPage.value && !isFetchingNextPage.value) {
        return fetchNextPage();
    }
}

// Surface the freshest highlight to the rotating LatestNotifications widget.
watch(highlights, (items) => {
    if (items.length) {
        busEmit('latest-notifications:new', {
            type: 'highlight',
            detail: items[0],
        });
    }
});

// Real-time highlights arrive over the bus without the admin-template catalog;
// prepend the raw payload onto the cached first page — useHighlights hydrates
// at read time with the relationships + templates from the first request.
on('game-dash:highlight', (data) => {
    queryClient.setQueriesData({ queryKey: ['gamedash', 'highlights'] }, (cache) => {
        if (!cache?.pages?.length) return cache;

        const [first, ...rest] = cache.pages;

        return {
            ...cache,
            pages: [
                { ...first, data: [data, ...(first.data || [])] },
                ...rest,
            ],
        };
    });
});
</script>

<template>
    <div>
        <div v-if="highlights.length" class="overflow-x-hidden">
            <UseVirtualList v-infinite-scroll="[loadMore, { distance: 200 }]" :list="highlights" :options="{ itemHeight: 100, overscan: 3 }" :height="height" class="pr-2 sb-tight sb-rounded sb-dust-700">
                <template #="{ data }">
                    <NotificationHighlight :detail="data" class="mb-2" />
                </template>
            </UseVirtualList>
        </div>
        <div v-else-if="isLoading" :style="{ height }" class="flex items-center justify-center">
            <NNerveLoading />
        </div>
        <div v-else :style="{ height }" class="flex items-center justify-center text-dust-500">
            {{ $t('component.game-dash.highlights.no-highlights') }}
        </div>
    </div>
</template>
